import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useBasket } from "../App";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { count } = useBasket();

  const handleOurStoryClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    if (location.pathname === '/') {
      // Already on homepage, just scroll
      document.getElementById('our-story')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to homepage then scroll
      navigate('/');
      setTimeout(() => {
        document.getElementById('our-story')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const links = [
    { path: "/collections", label: "Collections", isAnchor: false },
    { path: "/products", label: "All Products", isAnchor: false },
    { path: "/turnkey", label: "Services", isAnchor: false },
    { path: "/#our-story", label: "Our Story", isAnchor: true },
    { path: "/contact", label: "Contact", isAnchor: false },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] glass border-b border-white/10" data-testid="header">
        <div className="px-6 sm:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="font-['Playfair_Display'] text-xl text-white tracking-tight"
              data-testid="header-logo"
            >
              INTROZZI
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-12" data-testid="desktop-nav">
              {links.map((link) => (
                link.isAnchor ? (
                  <a
                    key={link.path}
                    href={link.path}
                    onClick={handleOurStoryClick}
                    className="text-sm tracking-wider transition-colors duration-300 text-gray-400 hover:text-white cursor-pointer"
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm tracking-wider transition-colors duration-300 ${
                      isActive(link.path) 
                        ? "text-white" 
                        : "text-gray-400 hover:text-white"
                    }`}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <Link
                to="/basket"
                className="relative text-gray-400 hover:text-white transition-colors"
                data-testid="nav-basket"
              >
                <ShoppingBag size={20} strokeWidth={1.5} />
                {count > 0 && (
                  <span className="absolute -top-2 -right-2 w-4 h-4 bg-[#C5A059] text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                    {count}
                  </span>
                )}
              </Link>
            </nav>

            {/* Mobile Right Icons */}
            <div className="flex items-center gap-4 md:hidden">
              <Link
                to="/basket"
                className="relative text-white p-2"
                data-testid="nav-basket-mobile"
              >
                <ShoppingBag size={22} strokeWidth={1.5} />
                {count > 0 && (
                  <span className="absolute top-0 right-0 w-4 h-4 bg-[#C5A059] text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                    {count}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="text-white p-2"
                data-testid="mobile-menu-btn"
              >
                <Menu size={24} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[150] bg-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex justify-between items-center border-b border-white/10">
              <span className="font-['Playfair_Display'] text-xl text-white">INTROZZI</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-white p-2"
                data-testid="close-mobile-menu"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>

            <nav className="px-6 py-12">
              <div className="space-y-8">
                {links.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {link.isAnchor ? (
                      <a
                        href={link.path}
                        onClick={handleOurStoryClick}
                        className="block text-2xl font-['Playfair_Display'] text-gray-400 hover:text-white cursor-pointer"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block text-2xl font-['Playfair_Display'] ${
                          isActive(link.path) ? "text-white" : "text-gray-400"
                        }`}
                      >
                        {link.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </nav>

            <div className="absolute bottom-12 left-6 right-6">
              <div className="border-t border-white/10 pt-8">
                <p className="text-gray-500 text-sm">contact@introzzi.ch</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
